import { IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { CustomNumberMaskedInput } from "../components/custom_input_number_mask";
import { CustomMoneyMaskedInput } from "../components/custom_input_money_mask";
import { CustomInput } from "../components/custom_input";

import CustomItemStatus from "../../../../../../common/components/custom_item_status";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * GenerateColetaPrecoFornecedorColumnDefinition
 */

type Props = {
  status: number | null;
  onQuantidadeColetaChange: (idColetaProdutoServico: number, value: number | null) => void;
  onValorUnitarioChange: (idColetaProdutoServico: number, value: number | null) => void;
  onObservacaoChange: (idColetaProdutoServico: number, value: string | null) => void;
  onActionEditar: (idColetaProdutoServico: number, openNewTab: boolean) => void;
  onActionDelete: (idColetaProdutoServico: number) => void;
};

export const GenerateColetaPrecoFornecedorColumnDefinition = ({
  status,
  onQuantidadeColetaChange,
  onValorUnitarioChange,
  onObservacaoChange,
  onActionEditar,
  onActionDelete,
}: Props): GridColDef[] => {
  const computeTipoTitle = (status: string) => {
    switch (status) {
      case 'P':
        return "Produto";
      case 'S':
        return "Serviço";
      default:
        return "?";
    }
  };

  const computeTipoColor = (status: string) => {
    switch (status) {
      case 'P':
        return "green";
      case 'S':
        return "yellow";
      default:
        return "blue";
    }
  };

  const computeTipoDescription = (status: string) => {
    switch (status) {
      case 'P':
        return "Produto";
      case 'S':
        return "Serviço";
      default:
        return "?";
    }
  };

  return [
    {
      field: "tipo",
      filterable: false,
      headerName: "Tipo",
      minWidth: 90,
      width: 90,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <CustomItemStatus
            size="autowidth"
            textSize="small"
            title={computeTipoTitle(value)}
            bgColor={computeTipoColor(value)}
            description={computeTipoDescription(value)}
          />
        </div>
      )
    },
    {
      field: "numero",
      filterable: false,
      headerName: "Nº",
      width: 50,
      minWidth: 50,
      sortable: false,
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Produto/Serviço",
      flex: 1,
      minWidth: 50,
      sortable: false,
    },
    {
      field: "unidade",
      filterable: false,
      headerName: "Unidade",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "quantidadeRequisicao",
      filterable: false,
      headerName: "Qtde Requisição",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "quantidadeOC",
      filterable: false,
      headerName: "Qtde OC",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "quantidadePendente",
      filterable: false,
      headerName: "Qtde Pendente",
      minWidth: 50,
      sortable: false,
    },
    {
      field: "quantidadeColeta",
      filterable: false,
      headerName: "Quantidade",
      width: 150,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => ([row['idColetaProdutoServico'], row['quantidadeColeta']]),
      renderCell: (({ value: [idColetaProdutoServico, quantidadeColeta] }) => {
        return (
          <CustomNumberMaskedInput
            value={quantidadeColeta}
            readOnly={status !== 0}
            onChange={value => onQuantidadeColetaChange(idColetaProdutoServico, value)}
          />
        );
      })
    },
    {
      field: "valorUnitario",
      filterable: false,
      headerName: "Valor Unitário",
      width: 150,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => ([row['idColetaProdutoServico'], row['valorUnitario']]),
      renderCell: (({ value: [idColetaProdutoServico, valorUnitario] }) => {
        return (
          <CustomMoneyMaskedInput
            value={valorUnitario}
            readOnly={status !== 0}
            onChange={value => onValorUnitarioChange(idColetaProdutoServico, value)}
          />
        );
      })
    },
    {
      field: "valorTotal",
      filterable: false,
      headerName: "Total",
      width: 150,
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => (
        <div>{value && <>R$ {value.toFixed(2).replace('.', ',')}</>}</div>
      ),
    },
    {
      field: "observacao",
      filterable: false,
      headerName: "Observação",
      width: 150,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => ([row['idColetaProdutoServico'], row['observacao']]),
      renderCell: (({ value: [idColetaProdutoServico, observacao] }) => {
        return (
          <CustomInput
            value={observacao}
            readOnly={status !== 0}
            onValueChange={value => onObservacaoChange(
              idColetaProdutoServico,
              value,
            )}
          />
        );
      })
    },
    {
      field: 'idColetaProdutoServico',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      width: 100,
      resizable: false,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton
              onMouseDown={e => {
                if (status === 0) {
                  if (e.button === 1) {
                    onActionEditar(value, true);
                  } else {
                    onActionEditar(value, e.ctrlKey);
                  }
                }
                e.preventDefault();
              }}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton
              onClick={() => {
                if (status === 0) {
                  onActionDelete(value);
                }
              }}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}